/**
 * New task screen — create a task and assign it to a teammate or a subcontractor company.
 *
 * Surfaces the assignment + notification feature that previously had no mobile front door
 * (backend supports assigned_to / subcontractor_id on tasks; assigning notifies the assignee).
 *
 * NEVER imports @smartbox/* (mobile invariant 1). Design-system primitives only; no raw styles.
 */
import { useState } from 'react';
import { StyleSheet } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createTask } from '../../../src/api/tasks';
import { listUsers } from '../../../src/api/account';
import { listSubcontractors } from '../../../src/api/subcontractors';
import { Screen, Text, Card, Button, Field, Select } from '../../../src/components/ui';
import { MicButton } from '../../../src/components/voice/MicButton';
import { theme } from '../../../src/theme';
import { extractErrorMessage } from '../../../src/lib/format';
import type { CreateTaskRequest, Priority } from '@beam/api-types';

const PRIORITY_OPTIONS: ReadonlyArray<{ value: Priority; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export default function NewTaskScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>('medium');
  const [dueAt, setDueAt] = useState('');
  const [assignee, setAssignee] = useState<string | null>(null); // "user:<id>" | "sub:<id>"
  const [formError, setFormError] = useState<string | null>(null);

  const usersQuery = useQuery({ queryKey: ['account-users'], queryFn: listUsers, staleTime: 60_000 });
  const subsQuery = useQuery({ queryKey: ['subcontractors'], queryFn: () => listSubcontractors({}), staleTime: 60_000 });

  const assigneeOptions = [
    ...(usersQuery.data ?? []).map((u) => ({ value: `user:${u.id}`, label: `${u.full_name} (${u.role ?? 'user'})` })),
    ...(subsQuery.data ?? []).map((s) => ({ value: `sub:${s.id}`, label: `${s.name} (subcontractor)` })),
  ];

  const createMutation = useMutation({
    mutationFn: (body: CreateTaskRequest) => createTask(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['work-queue'] });
      router.back();
    },
    onError: (err) => setFormError(extractErrorMessage(err, 'Could not create task.')),
  });

  const onCreate = () => {
    setFormError(null);
    if (!title.trim()) {
      setFormError('Enter a task title.');
      return;
    }
    const body: CreateTaskRequest = { title: title.trim(), priority };
    if (description.trim()) body.description = description.trim();
    if (dueAt.trim()) body.due_at = dueAt.trim();
    if (assignee?.startsWith('user:')) body.assigned_to = assignee.slice('user:'.length);
    else if (assignee?.startsWith('sub:')) body.subcontractor_id = assignee.slice('sub:'.length);
    createMutation.mutate(body);
  };

  return (
    <Screen scroll>
      <Text variant="title">New task</Text>
      <Field label="Title" placeholder="e.g. Order conduit for Smith job" value={title} onChangeText={setTitle} accessibilityLabel="Task title" />
      <MicButton
        onTranscribe={(t) => setTitle((prev) => (prev ? `${prev} ${t}` : t))}
        disabled={createMutation.isPending}
        label="Dictate title"
        accessibilityLabel="Dictate task title"
      />
      <Field label="Details (optional)" placeholder="Notes" value={description} onChangeText={setDescription} multiline accessibilityLabel="Task details" />
      <MicButton
        onTranscribe={(t) => setDescription((prev) => (prev ? `${prev} ${t}` : t))}
        disabled={createMutation.isPending}
        label="Dictate details"
        accessibilityLabel="Dictate task details"
      />
      <Select label="Priority" options={PRIORITY_OPTIONS} value={priority} onChange={(v) => setPriority(v)} />
      <Field label="Due (optional, YYYY-MM-DD)" placeholder="2026-07-01" value={dueAt} onChangeText={setDueAt} autoCapitalize="none" accessibilityLabel="Due date" />
      <Select
        label="Assign to (optional)"
        options={assigneeOptions}
        value={assignee}
        onChange={setAssignee}
      />
      {assigneeOptions.length === 0 && !usersQuery.isLoading ? (
        <Text variant="small" color="secondary">No teammates or subcontractors to assign yet.</Text>
      ) : null}

      {formError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">{formError}</Text>
        </Card>
      ) : null}

      <Button title="Create task" onPress={onCreate} loading={createMutation.isPending} disabled={createMutation.isPending} accessibilityLabel="Create task" />
    </Screen>
  );
}

const styles = StyleSheet.create({
  _unused: { gap: theme.spacing.sm },
});
